import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  CatalogBlock,
  CatalogRow,
  CatalogValue,
  cardCatalogIsVisible,
  catalogLocale,
  presentCardCatalog,
} from '../lib/card_catalog_facts'
import { Item } from '../types'
import '../stylesheets/CardCatalog.scss'

type Props = Readonly<{
  item: Item
  pageUrl?: string
}>

const CatalogValueView = ({ value }: { value: CatalogValue }): JSX.Element => {
  if (!value.href) return <>{ value.text }</>
  if (value.href.startsWith('/')) return <Link to={ value.href }>{ value.text }</Link>
  return <a href={ value.href } rel="noreferrer">{ value.text }</a>
}

const CatalogValues = ({ values, separator }: { values: CatalogValue[], separator: string }): JSX.Element => {
  return (
    <>
      {
        values.map((value, index) => (
          <span key={ `${value.href ?? ''}:${value.text}:${index}` }>
            { index > 0 ? separator : null }
            <CatalogValueView value={ value } />
          </span>
        ))
      }
    </>
  )
}

const CatalogBlocks = ({ blocks }: { blocks: CatalogBlock[] }): JSX.Element => {
  return (
    <ul className="card-catalog-attacks">
      {
        blocks.map((block, index) => (
          <li key={ `${block.name ?? ''}:${index}` }>
            { block.name ? <span className="card-catalog-attack-name">{ block.name }</span> : null }
            { block.meta ? <span className="card-catalog-attack-cost">{ block.meta }</span> : null }
            { block.text ? <p className="card-catalog-prose">{ block.text }</p> : null }
          </li>
        ))
      }
    </ul>
  )
}

const CatalogRowView = ({ row, separator }: { row: CatalogRow, separator: string }): JSX.Element => {
  return (
    <div className="card-catalog-row" data-catalog-property={ row.propertyId }>
      <dt>{ row.label }</dt>
      <dd>
        { row.values ? <CatalogValues values={ row.values } separator={ separator } /> : null }
        { row.blocks ? <CatalogBlocks blocks={ row.blocks } /> : null }
      </dd>
    </div>
  )
}

const currentPageUrl = (): string | undefined => {
  if (typeof window === 'undefined') return
  return window.location.origin + window.location.pathname
}

const CardCatalogFacts = ({ item, pageUrl }: Props): JSX.Element | null => {
  const { i18n, t } = useTranslation()
  const locale = catalogLocale(i18n.resolvedLanguage ?? i18n.language)
  const resolvedUrl = pageUrl ?? currentPageUrl()

  const presented = useMemo(() => {
    return presentCardCatalog(item, locale, {
      translate: (key) => {
        const value = t(key)
        return typeof value === 'string' ? value : key
      },
      pageUrl: resolvedUrl,
    })
  }, [item, locale, resolvedUrl, t])

  if (!cardCatalogIsVisible(presented)) return null

  const jsonLd = presented.jsonLd
    ? JSON.stringify(presented.jsonLd).replace(/</g, '\\u003c')
    : undefined

  return (
    <section className="card-catalog" id="card-catalog" aria-labelledby="card-catalog-heading">
      <h2 id="card-catalog-heading">{ presented.heading }</h2>
      { presented.description ? <p className="card-catalog-description">{ presented.description }</p> : null }
      { presented.intro ? <p className="card-catalog-intro">{ presented.intro }</p> : null }
      {
        presented.rows.length > 0
          ? (
            <dl>
              { presented.rows.map((row) => <CatalogRowView key={ row.propertyId } row={ row } separator={ presented.separator } />) }
            </dl>
          )
          : null
      }
      {
        jsonLd
          ? <script type="application/ld+json" dangerouslySetInnerHTML={ { __html: jsonLd } } />
          : null
      }
    </section>
  )
}

export { CardCatalogFacts }
